import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useNavContext } from "../context/nav_context";
import { Loading } from "../components";
import { TfiAngleLeft } from "react-icons/tfi";
import { FiAward, FiSave, FiArrowUp, FiArrowDown, FiSearch, FiX, FiChevronDown } from "react-icons/fi";
import {
  api_v3_get_tournament_by_id, api_v3_list_of_players_admin,
  api_v1_get_hall_of_fame, api_v1_save_hall_of_fame, api_v3_get_classification,
} from "../utils/constants";

const initDivisionState = (divisions) =>
  divisions.map((div) => ({ division_name: div.name, divisionId: div.divisionId, champion_id: "", promotions: [], relegations: [] }));

const ChampionCombobox = ({ players, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const selected = players.find((p) => p.playerId === value);

  useEffect(() => {
    const handle = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setSearch(""); } };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = search.trim() === "" ? players : players.filter((p) => p.username.toLowerCase().includes(search.toLowerCase()));

  return (
    <CBoxWrap ref={wrapperRef}>
      <CBoxTrigger onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} $has={!!value}>
        <span>{selected ? selected.username : "— Ninguno —"}</span>
        <FiChevronDown style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </CBoxTrigger>
      {value && <CBoxClear title="Quitar campeón" onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}><FiX size={13} /></CBoxClear>}
      {open && (
        <CBoxDropdown>
          <CBoxSearch><FiSearch size={13} /><input ref={inputRef} placeholder="Buscar jugador..." value={search} onChange={(e) => setSearch(e.target.value)} /></CBoxSearch>
          <CBoxList>
            <CBoxOpt $sel={value === ""} onClick={() => { onChange(""); setOpen(false); setSearch(""); }}>— Ninguno —</CBoxOpt>
            {filtered.length === 0 && <CBoxEmpty>Sin resultados</CBoxEmpty>}
            {filtered.map((p) => (
              <CBoxOpt key={p.playerId} $sel={p.playerId === value} onClick={() => { onChange(p.playerId); setOpen(false); setSearch(""); }}>{p.username}</CBoxOpt>
            ))}
          </CBoxList>
        </CBoxDropdown>
      )}
    </CBoxWrap>
  );
};

const MultiSelectCombobox = ({ players, selected, onToggle, color }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setSearch(""); } };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = search.trim() === "" ? players : players.filter((p) => p.username.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <CBoxWrap ref={wrapperRef}>
        <CBoxTrigger onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} $has={selected.length > 0}>
          <span>+ Seleccionar jugador...</span>
          <FiChevronDown style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
        </CBoxTrigger>
        {open && (
          <CBoxDropdown>
            <CBoxSearch><FiSearch size={13} /><input ref={inputRef} placeholder="Buscar jugador..." value={search} onChange={(e) => setSearch(e.target.value)} /></CBoxSearch>
            <CBoxList>
              {filtered.length === 0 && <CBoxEmpty>Sin resultados</CBoxEmpty>}
              {filtered.map((p) => (
                <CBoxOpt key={p.playerId} $sel={selected.includes(p.playerId)} onClick={() => onToggle(p.playerId)}>
                  {p.username} {selected.includes(p.playerId) && " ✓"}
                </CBoxOpt>
              ))}
            </CBoxList>
          </CBoxDropdown>
        )}
      </CBoxWrap>
      {selected.length > 0 && (
        <SelBadgeRow>
          {selected.map((pid) => {
            const p = players.find((x) => x.playerId === pid);
            if (!p) return null;
            return (
              <SelBadge key={pid} $col={color}>
                {p.username}
                <SelBadgeBtn onClick={() => onToggle(pid)}><FiX size={12} /></SelBadgeBtn>
              </SelBadge>
            );
          })}
        </SelBadgeRow>
      )}
    </div>
  );
};

const AdminHallOfFame = () => {
  const { tournamentId } = useParams();
  const { user, accessToken } = useNavContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [error, setError] = useState(null);
  const [tournamentInfo, setTournamentInfo] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerMap, setPlayerMap] = useState({});
  const [divisions, setDivisions] = useState([]);
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true); setError(null);
    try {
      const tRes = await axios.get(api_v3_get_tournament_by_id(tournamentId), { headers: { Authorization: `Bearer ${accessToken}` } });
      const tInfo = tRes.data; setTournamentInfo(tInfo);
      const pRes = await axios.get(api_v3_list_of_players_admin(tournamentId, false), { headers: { Authorization: `Bearer ${accessToken}` } });
      const approved = pRes.data.filter((p) => p.tournamentHistory.some((h) => h.tournamentId === tournamentId && h.status === "APPROBED"));
      approved.sort((a, b) => a.username.localeCompare(b.username));
      setPlayers(approved);
      const pMap = {}; approved.forEach((p) => { pMap[p.playerId] = p; }); setPlayerMap(pMap);
      const sortedDivisions = [...(tInfo.divisions || [])].sort((a, b) => a.sequence - b.sequence);
      let initialState = initDivisionState(sortedDivisions);
      try {
        const hofRes = await axios.get(api_v1_get_hall_of_fame(tournamentId), { headers: { Authorization: `Bearer ${accessToken}` } });
        if (hofRes.data && hofRes.data.divisions) {
          initialState = sortedDivisions.map((div) => {
            const saved = hofRes.data.divisions.find((d) => d.division_name === div.name);
            if (!saved) return { division_name: div.name, divisionId: div.divisionId, champion_id: "", promotions: [], relegations: [] };
            return { division_name: div.name, divisionId: div.divisionId, champion_id: saved.champion?.playerId || "", promotions: (saved.promotions || []).map((p) => p.playerId).filter(Boolean), relegations: (saved.relegations || []).map((p) => p.playerId).filter(Boolean) };
          });
        }
      } catch (_) {}
      setDivisions(initialState);
    } catch (err) {
      console.error("Error loading Hall of Fame data:", err);
      setError("Error al cargar los datos. Comprueba que el torneo existe.");
    } finally { setIsLoading(false); }
  }, [tournamentId, accessToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoFill = async () => {
    if (!tournamentInfo) return;
    setIsAutoFilling(true);
    try {
      const sourceDivs = tournamentInfo.divisions || [];
      const newDivisionsState = [...divisions];
      for (let i = 0; i < newDivisionsState.length; i++) {
        const currentDiv = newDivisionsState[i];
        const sourceDiv = sourceDivs.find((d) => d.divisionId === currentDiv.divisionId);
        if (!sourceDiv || !sourceDiv.groups) continue;
        let firstChampionSet = false;
        const newPromotions = []; const newRelegations = [];
        for (const group of sourceDiv.groups) {
          try {
            const clsRes = await axios.get(api_v3_get_classification(sourceDiv.divisionId, group.groupId), { headers: { Authorization: `Bearer ${accessToken}` } });
            const playersData = clsRes.data.players || [];
            const sorted = [...playersData].sort((a, b) => { if (a.position === null) return 1; if (b.position === null) return -1; return a.position - b.position; });
            const valid = sorted.filter((p) => p.position !== null);
            if (valid.length > 0) {
              const firstId = valid[0].playerId; const lastId = valid[valid.length - 1].playerId;
              if (sourceDiv.groups.length === 1 && !firstChampionSet) { currentDiv.champion_id = firstId; firstChampionSet = true; }
              if (!newPromotions.includes(firstId)) newPromotions.push(firstId);
              if (!newRelegations.includes(lastId)) newRelegations.push(lastId);
            }
          } catch (err) { console.error(err); }
        }
        currentDiv.promotions = Array.from(new Set([...currentDiv.promotions, ...newPromotions]));
        currentDiv.relegations = Array.from(new Set([...currentDiv.relegations, ...newRelegations]));
      }
      setDivisions(newDivisionsState);
      setSaveMsg({ type: "success", text: "Auto-fill completado. No olvides guardar." });
    } catch (err) {
      console.error(err);
      setSaveMsg({ type: "error", text: "Error en el auto-fill." });
    } finally { setIsAutoFilling(false); setTimeout(() => setSaveMsg(null), 4000); }
  };

  const handleSave = async () => {
    setIsSaving(true); setSaveMsg(null);
    try {
      const payload = { divisions: divisions.map((d) => ({ division_name: d.division_name, champion_id: d.champion_id || null, promotions: d.promotions, relegations: d.relegations })) };
      await axios.put(api_v1_save_hall_of_fame(tournamentId), payload, { headers: { Authorization: `Bearer ${accessToken}` } });
      setSaveMsg({ type: "success", text: "Hall of Fame guardado correctamente ✓" });
    } catch (err) { console.error("Error saving Hall of Fame:", err); setSaveMsg({ type: "error", text: "Error al guardar el Hall of Fame." }); }
    finally { setIsSaving(false); setTimeout(() => setSaveMsg(null), 4000); }
  };

  const setChampion = (divIdx, playerId) => setDivisions((prev) => prev.map((d, i) => (i === divIdx ? { ...d, champion_id: playerId } : d)));
  const togglePlayer = (divIdx, field, playerId) => setDivisions((prev) => prev.map((d, i) => {
    if (i !== divIdx) return d;
    const list = d[field];
    const exists = list.includes(playerId);
    return { ...d, [field]: exists ? list.filter((id) => id !== playerId) : [...list, playerId] };
  }));

  if (!user) return <Canvas><EmptyState>Debes iniciar sesión para acceder a esta página.</EmptyState></Canvas>;
  if (!user.isAdmin && !user.isModerator) return <Canvas><EmptyState>Lo siento, no tienes permiso para acceder aquí.</EmptyState></Canvas>;
  if (isLoading) return <Canvas><Loading /></Canvas>;

  return (
    <Canvas>
      <Content>
        <HeaderRow>
          <PageTitle><FiAward style={{ marginRight: "0.5rem", verticalAlign: "middle" }} /> HALL OF FAME</PageTitle>
          <HeaderActions>
            <BackLink to="/admin/"><TfiAngleLeft /> ADMIN HOME</BackLink>
            <SaveBtn onClick={handleSave} disabled={isSaving}><FiSave /> {isSaving ? "GUARDANDO..." : "GUARDAR"}</SaveBtn>
            <AutoBtn onClick={handleAutoFill} disabled={isAutoFilling}>{isAutoFilling ? "CARGANDO..." : "AUTO-FILL"}</AutoBtn>
          </HeaderActions>
        </HeaderRow>
        <Subtitle>{tournamentInfo?.name || `Torneo ${tournamentId}`}</Subtitle>
        {error && <AlertBox $type="error">{error}</AlertBox>}
        {saveMsg && <AlertBox $type={saveMsg.type}>{saveMsg.text}</AlertBox>}

        {divisions.map((div, divIdx) => (
          <DivCard key={div.divisionId}>
            <DivCardTitle>{div.division_name}</DivCardTitle>

            <FieldGroup>
              <FieldLabel><ChampIcon>🏆</ChampIcon> CAMPEÓN</FieldLabel>
              <ChampRow>
                <ChampionCombobox players={players} value={div.champion_id} onChange={(pid) => setChampion(divIdx, pid)} />
                {div.champion_id && playerMap[div.champion_id] && <ChampBadge>🥇 {playerMap[div.champion_id].username}</ChampBadge>}
              </ChampRow>
            </FieldGroup>

            <TwoCol>
              <FieldGroup>
                <FieldLabel><FiArrowUp style={{ color: "var(--v-mint)" }} /> ASCENSOS {div.promotions.length > 0 && <CountBadge $col="mint">{div.promotions.length}</CountBadge>}</FieldLabel>
                <MultiSelectCombobox players={players} selected={div.promotions} onToggle={(pid) => togglePlayer(divIdx, "promotions", pid)} color="mint" />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel><FiArrowDown style={{ color: "var(--v-uv)" }} /> DESCENSOS {div.relegations.length > 0 && <CountBadge $col="uv">{div.relegations.length}</CountBadge>}</FieldLabel>
                <MultiSelectCombobox players={players} selected={div.relegations} onToggle={(pid) => togglePlayer(divIdx, "relegations", pid)} color="uv" />
              </FieldGroup>
            </TwoCol>

            <SummaryRow>
              <SumTag $col="mint">↑ {div.promotions.length} ascensos</SumTag>
              <SumTag $col="uv">↓ {div.relegations.length} descensos</SumTag>
            </SummaryRow>
          </DivCard>
        ))}

        <BottomSave>
          <SaveBtn onClick={handleSave} disabled={isSaving}><FiSave /> {isSaving ? "GUARDANDO..." : "GUARDAR HALL OF FAME"}</SaveBtn>
        </BottomSave>
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 900px;`;
const PageTitle = styled.h1`font-family: Anton, sans-serif; font-size: clamp(32px, 5vw, 48px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0;`;
const EmptyState = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;
const HeaderRow = styled.div`display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem; flex-wrap: wrap; gap: 1rem;`;
const HeaderActions = styled.div`display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;`;
const Subtitle = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); margin: 0 0 2rem;`;
const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.45rem 0.85rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.04); }`;
const SaveBtn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; background: var(--v-mint); border: none; color: #000; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1.25rem; border-radius: 24px; cursor: pointer; transition: opacity 0.15s ease; &:disabled { opacity: 0.4; cursor: not-allowed; } &:hover:not(:disabled) { opacity: 0.85; }`;
const AutoBtn = styled.button`background: #c9a04e; border: none; color: #000; font-family: var(--v-font-mono); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem 1rem; border-radius: 24px; cursor: pointer; transition: opacity 0.15s ease; &:disabled { opacity: 0.4; cursor: not-allowed; } &:hover:not(:disabled) { opacity: 0.85; }`;
const AlertBox = styled.div`padding: 0.65rem 1rem; border-radius: 12px; margin-bottom: 1rem; font-family: var(--v-font-mono); font-size: 11px; background: ${({ $type }) => $type === "success" ? "rgba(60,255,208,0.08)" : "rgba(255,59,59,0.1)"}; border: 1px solid ${({ $type }) => $type === "success" ? "rgba(60,255,208,0.15)" : "rgba(255,59,59,0.2)"}; color: ${({ $type }) => $type === "success" ? "var(--v-mint)" : "#ff3b3b"};`;
const DivCard = styled.div`background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 1.5rem; margin-bottom: 1rem;`;
const DivCardTitle = styled.h3`font-family: var(--v-font-body); font-size: 16px; font-weight: 600; color: var(--v-fg); margin: 0 0 1rem; padding-bottom: 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.04);`;
const FieldGroup = styled.div`margin-bottom: 1rem;`;
const FieldLabel = styled.label`display: flex; align-items: center; gap: 0.35rem; font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); margin-bottom: 0.5rem;`;
const ChampIcon = styled.span`font-size: 14px;`;
const ChampRow = styled.div`display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;`;
const ChampBadge = styled.div`display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.25rem 0.7rem; background: rgba(201,160,78,0.15); border: 1px solid rgba(201,160,78,0.3); color: #c9a04e; border-radius: 20px; font-family: var(--v-font-body); font-size: 13px; font-weight: 600;`;
const TwoCol = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; @media (max-width: 640px) { grid-template-columns: 1fr; }`;
const CountBadge = styled.span`display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; font-size: 10px; font-weight: 700; background: ${({ $col }) => $col === "mint" ? "rgba(60,255,208,0.15)" : "rgba(82,0,255,0.15)"}; color: ${({ $col }) => $col === "mint" ? "var(--v-mint)" : "var(--v-uv)"}; margin-left: 4px;`;
const SummaryRow = styled.div`display: flex; gap: 0.75rem; margin-top: 0.75rem;`;
const SumTag = styled.span`padding: 0.2rem 0.65rem; border-radius: 20px; font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; background: ${({ $col }) => $col === "mint" ? "rgba(60,255,208,0.08)" : "rgba(82,0,255,0.08)"}; border: 1px solid ${({ $col }) => $col === "mint" ? "rgba(60,255,208,0.15)" : "rgba(82,0,255,0.15)"}; color: ${({ $col }) => $col === "mint" ? "var(--v-mint)" : "var(--v-uv)"};`;
const BottomSave = styled.div`display: flex; justify-content: flex-end; margin-top: 1rem;`;

/* Combobox */
const CBoxWrap = styled.div`position: relative; display: inline-flex; align-items: center; gap: 4px;`;
const CBoxTrigger = styled.button`display: inline-flex; align-items: center; justify-content: space-between; gap: 0.5rem; min-width: 200px; padding: 0.4rem 0.75rem; background: rgba(255,255,255,0.04); color: ${({ $has }) => $has ? "var(--v-fg)" : "var(--v-fg-muted)"}; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; font-family: var(--v-font-body); font-size: 13px; cursor: pointer; transition: border-color 0.15s ease; &:hover, &:focus { border-color: var(--v-mint); outline: none; }`;
const CBoxClear = styled.button`display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; padding: 0; background: rgba(255,255,255,0.06); border: none; border-radius: 50%; color: var(--v-fg-muted); cursor: pointer; transition: background 0.15s, color 0.15s; &:hover { background: rgba(255,59,59,0.15); color: #ff3b3b; }`;
const CBoxDropdown = styled.div`position: absolute; top: calc(100% + 4px); left: 0; z-index: 999; width: 280px; background: var(--v-surface); border: 1px solid rgba(255,255,255,0.08); border-radius: 14px; box-shadow: 0 8px 24px rgba(0,0,0,0.5); overflow: hidden;`;
const CBoxSearch = styled.div`display: flex; align-items: center; gap: 0.4rem; padding: 0.5rem 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.04); color: var(--v-fg-muted); input { flex: 1; background: transparent; border: none; color: var(--v-fg); font-family: var(--v-font-body); font-size: 13px; outline: none; &::placeholder { color: var(--v-fg-muted); opacity: 0.5; } }`;
const CBoxList = styled.div`max-height: 220px; overflow-y: auto; &::-webkit-scrollbar { width: 4px; } &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }`;
const CBoxOpt = styled.div`padding: 0.4rem 0.85rem; font-family: var(--v-font-body); font-size: 13px; cursor: pointer; background: ${({ $sel }) => $sel ? "rgba(60,255,208,0.08)" : "transparent"}; color: ${({ $sel }) => $sel ? "var(--v-mint)" : "var(--v-fg)"}; border-bottom: 1px solid rgba(255,255,255,0.03); transition: background 0.12s; &:hover { background: rgba(60,255,208,0.04); } &:last-child { border-bottom: none; }`;
const CBoxEmpty = styled.div`color: var(--v-fg-muted); font-style: italic; font-size: 12px; text-align: center; padding: 0.5rem;`;

const SelBadgeRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const SelBadge = styled.div`display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.2rem 0.55rem; background: ${({ $col }) => $col === "mint" ? "rgba(60,255,208,0.08)" : "rgba(82,0,255,0.08)"}; color: ${({ $col }) => $col === "mint" ? "var(--v-mint)" : "var(--v-uv)"}; border: 1px solid ${({ $col }) => $col === "mint" ? "rgba(60,255,208,0.2)" : "rgba(82,0,255,0.2)"}; border-radius: 12px; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600;`;
const SelBadgeBtn = styled.button`display: inline-flex; background: none; border: none; padding: 0; color: inherit; opacity: 0.5; cursor: pointer; &:hover { opacity: 1; }`;

export default AdminHallOfFame;
